import React, { type ReactNode, useMemo, type RefObject } from "react";
import { type XYCoord } from "react-dnd";
import { connect } from "react-redux";
import { RemovableCard } from "../../../../../components/cards";
import AssetTypes from "../../../../../enums/assetTypes";
import { type RootState } from "../../../../Application/globaltypes/redux";
import MultipleDragCardView from "./MultipleDragCardView/MultipleDragCardView";
import { noop } from "lodash";

import classStyles from "./packAssetDragLayerView.module.scss";
import { Icons, RenderIcons } from "components/dropdowns/contentSegmentationDropdown/constants";
import { Icon, type SemanticICONS } from "semantic-ui-react";
import { ContentTypesEnum } from "enums";

const NAME_TRANSLATE: Record<string, ContentTypesEnum> = {
  // Assets
  Video: ContentTypesEnum.Videos,
  EmbeddedVideo: ContentTypesEnum["Embedded Videos"],
  Assessment: ContentTypesEnum.Assessments,
  Survey: ContentTypesEnum.Surveys,
  Pdf: ContentTypesEnum.PDFs,
  Link: ContentTypesEnum.Links,

  // Communications
  Email: ContentTypesEnum.Emails,
  Message: ContentTypesEnum.Messages,

  // Events
  ExternalEvent: ContentTypesEnum.Events,

  // Flows
  Flow: ContentTypesEnum.Flows,

  // Campaigns (TD add-on)
  Campaign: ContentTypesEnum.Campaigns,
};

function getIcon(type?: string): ReactNode {
  if (type && NAME_TRANSLATE[type]) {
    if (Icons[NAME_TRANSLATE[type]]) {
      return Icons[NAME_TRANSLATE[type]];
    } else if (RenderIcons[NAME_TRANSLATE[type]]) {
      return RenderIcons[NAME_TRANSLATE[type]]!();
    }
  }
  return "fa-file";
}

function createDescription(type?: string) {
  switch (type) {
    case "ExternalEvent":
      return "Event";
    case AssetTypes.Pdf:
      return "PDF";
    case AssetTypes.EmbeddedVideo:
      return "Embedded Video";
    default:
      return type;
  }
}

function getMeta(type?: string) {
  if (!type) {
    return null;
  }
  const iconName = getIcon(type);
  if (typeof iconName === "string") {
    return (
      <>
        <Icon name={iconName as SemanticICONS} /> {createDescription(type)}
      </>
    );
  }
  return (
    <>
      {iconName} {createDescription(type)}
    </>
  );
}

function getItemStyles(container: RefObject<HTMLDivElement>, clientOffset: XYCoord | null, cardWidth?: number) {
  if (!clientOffset) {
    return {
      display: "none",
      zIndex: 999,
    };
  }

  const containerBounding = container?.current?.getBoundingClientRect() || {
    left: 0,
    top: 0,
  };

  const left = Math.round(clientOffset.x) - containerBounding.left - 20; // Adjust offset as needed
  const top = Math.round(clientOffset.y) - containerBounding.top - 20;

  return {
    width: cardWidth ? `${Math.max(cardWidth - 23, 1)}px` : "55vw",
    height: "5.1rem",
    transform: `translate(${left}px, ${top}px)`,
    transformOrigin: "top left",
    zIndex: 999,
  };
}

export interface Props {
  item: any;
  clientOffset: XYCoord | null;
  container: React.RefObject<HTMLDivElement>;
  selectedCardsCount: number;
  cardWidth?: number;
}

export const PackAssetDragLayerView: React.FC<Props> = ({
  item,
  clientOffset,
  container,
  selectedCardsCount,
  cardWidth,
}) => {
  const styles = getItemStyles(container, clientOffset, cardWidth);

  const body = useMemo(() => {
    if (selectedCardsCount > 1) {
      return <MultipleDragCardView count={selectedCardsCount} />;
    }

    return (
      <RemovableCard
        id={item?.id}
        title={item?.title}
        type={item?.type}
        publisher={item?.publisher}
        thumbnailUrl={item?.thumbnailUrl}
        meta={getMeta(item?.elementType)}
        hidden={item?.visibility === false}
        onRemoveItemClick={noop}
        className={classStyles.draggingCard}
      />
    );
  }, [
    item?.id,
    item?.thumbnailUrl,
    item?.title,
    item?.elementType,
    item?.type,
    item?.visibility,
    item?.publisher,
    selectedCardsCount,
  ]);

  return <div style={styles}>{body}</div>;
};

const mapStateToProps = (state: RootState) => ({
  selectedCardsCount: state.packs.packDetailsReducer.selectedCards.length,
});

export default connect(mapStateToProps)(PackAssetDragLayerView);
